import React, { useEffect, useState } from 'react';

import { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next';
import Head from 'next/head';
import { format } from 'date-fns';
import parse from 'html-react-parser';
import styled from 'styled-components';
import ru from 'date-fns/locale/ru';

import { Text, Divider, ImageContainer, Carousel, Link } from 'atoms';
import { SocialSharing } from 'molecules';
import { WinnersApi } from 'services';
import { convertWinnerToApp } from 'mappers';
import { colors, media_sm, media_lg, media_md } from 'theme';
import { routes } from 'core';

export async function getServerSideProps({ query }: GetServerSidePropsContext) {
  const { slug } = query;

  const result = await WinnersApi.getBySlug(slug);
  const winner = convertWinnerToApp(result);

  return {
    props: {
      winner,
    },
  };
}

export default function Winner({ winner }: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const [url, setUrl] = useState('');

  useEffect(() => {
    setUrl(window.location.href);
  }, [winner]);

  return (
    <>
      <Head>
        <title>{winner.seoTitle ?? winner.title}</title>
        <meta name="description" content={winner.seoDescription ?? winner.title} />

        <meta property="og:type" content="article" />
        <meta property="og:title" content={winner.title} />
        <meta property="og:description" content={winner.seoDescription || ''} />
        {winner.titleImage && <meta property="og:image" content={winner.titleImage} key="image" />}
      </Head>
      <HeaderInfo>
        <LinkToAll
          href={{
            pathname: routes.winners,
            query: { category: 'all' },
          }}
        >
          Победители
        </LinkToAll>
        <Text color={colors.lightGrey} fontSize="16px">
          {format(new Date(winner.date), 'dd MMMM yyyy', { locale: ru })}
        </Text>
      </HeaderInfo>
      <ParagraphStyled>{winner.title}</ParagraphStyled>
      {winner.slider.length > 0 ? (
        <Carousel autoplay>
          {winner.slider.map((item) => (
            <CarouselImage key={item.id} src={item.imageUrl} alt={winner.title} />
          ))}
        </Carousel>
      ) : (
        winner.titleImage && <ImageContainerStyled alt={winner.title} src={winner.titleImage} />
      )}

      <ParsedDescription>{parse(winner.firstDescription)}</ParsedDescription>

      {winner.videoUrl && (
        <Video
          src={winner.videoUrl.replace('watch?v=', 'embed/')}
          title="YouTube video player"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      )}
      <ParsedDescription>{parse(winner.secondDescription)}</ParsedDescription>
      <DividerStyled />
      <SocialSharingStyled url={url} title={winner.title} />
    </>
  );
}

const HeaderInfo = styled.div`
  display: flex;
  justify-content: space-between;

  ${media_sm} {
    justify-content: flex-start;
    span:last-child {
      margin-left: 20px;
    }
  }
`;

const LinkToAll = styled(Link)`
  color: ${colors.pink};
  font-size: 16px;

  &:hover {
    color: ${colors.pink};
    text-decoration: none;
  }
`;

const ParagraphStyled = styled.h1`
  color: ${colors.mainText};
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 0px;

  ${media_sm} {
    margin-top: 8px;
    font-size: 50px;
    line-height: 65px;
  }
`;

const ImageContainerStyled = styled(ImageContainer)`
  height: 190px;
  margin-top: 40px;
  margin-bottom: 40px;

  border-radius: 20px;

  ${media_sm} {
    height: 440px;
    margin-top: 13px;
    margin-bottom: 54px;
  }
`;

const CarouselImage = styled(ImageContainer)`
  height: 190px;

  border-radius: 20px;

  ${media_sm} {
    height: 440px;
  }
`;

const Video = styled.iframe`
  width: 100%;
  height: 170px;
  border-radius: 20px;
  margin-bottom: 40px;

  ${media_sm} {
    height: 400px;
  }

  ${media_md} {
    height: 420px;
  }

  ${media_lg} {
    height: 640px;
  }
`;

const ParsedDescription = styled.div`
  padding: 0;

  ul,
  ol {
    list-style-position: outside;
  }

  & li {
    margin-left: 30px;
  }

  img {
    width: 100% !important;
    height: auto !important;
    border-radius: 20px;
  }

  *:not(a):not(a *) {
    font-size: 18px;
    color: ${colors.mainText} !important;
    background-color: transparent !important;
  }

  ${media_lg} {
    > :not(img) {
      margin-left: 200px;
      margin-right: 200px;
    }

    p > img {
      width: calc(100% + 400px) !important;
      height: auto !important;
      margin-left: -200px;
    }
  }
`;

const DividerStyled = styled(Divider)`
  ${media_lg} {
    margin-top: 52px;
    margin-left: 200px;
    margin-right: 200px;
  }
`;

const SocialSharingStyled = styled(SocialSharing)`
  padding-bottom: 40px;
  ${media_lg} {
    margin-left: 200px;
    margin-right: 200px;
    margin-top: 43px;
    margin-bottom: 44px;
  }
`;
